import type { FileType } from "@/lib/types";

/**
 * Classify a picked file into one of the backend's FILE_TYPES.
 * Prefers the browser-provided MIME type; falls back to extension
 * sniffing when MIME is missing (some platforms leave it blank for
 * uncommon types).
 *
 * Mapping is intentionally coarse — the backend stores nothing more
 * specific than image/video/audio/document/other on the row.
 */
export function detectFileType(file: File): FileType {
  const mime = (file.type || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime && (
    mime === "application/pdf" ||
    mime === "application/msword" ||
    mime === "text/csv" ||
    mime.startsWith("text/") ||
    mime.startsWith("application/vnd.openxmlformats-officedocument") ||
    mime.startsWith("application/vnd.ms-") ||
    mime.startsWith("application/vnd.oasis.opendocument") ||
    mime === "application/rtf"
  )) return "document";

  // Fall back to extension when MIME wasn't helpful.
  const name = file.name.toLowerCase();
  const ext  = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";

  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "tiff", "ico"].includes(ext)) return "image";
  if (["mp4", "mov", "avi", "mkv", "webm", "m4v", "wmv", "flv"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "m4a", "aac", "flac", "opus"].includes(ext)) return "audio";
  if (["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "ods",
       "odp", "rtf", "txt", "md", "csv", "tsv"].includes(ext)) return "document";

  return "other";
}
